import { useCallback, useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/macro';

import { useApiPetCalloutResponseCreate } from '@rover/rsdk/src/apiClient/latest/api-pet-callout-response-create';
import { useAuthedUserSummary } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import type { Callout, PetInfoQuestion, PetInfoQuestionCallout } from '@rover/types/src/Callout';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import {
  DogSet,
  getCalloutResponse,
  hasRespondedToCallout,
  storeCalloutResponse,
} from './petInfoCalloutStorage';

export const CALLOUT_IDS = {
  DOG_INTERACTION: 'dog-interaction',
  DOG_RESPONSE_CONFIRMATION: 'dog-response-confirmation',
} as const;

export const CALLOUT_RESPONSES = {
  YES: 'yes',
  NO: 'no',
  UNSURE: 'unsure',
  DISMISSED: 'dismiss',
} as const;

export const extractPetInfoCallout = (
  searchCallouts?: Callout[]
): PetInfoQuestionCallout | undefined =>
  searchCallouts?.find(({ slug }) => slug === 'pet-info-callout') as
    | PetInfoQuestionCallout
    | undefined;

type UsePetInfoCalloutCoreParams = {
  callouts: Callout[];
  dogSet: DogSet;
  updateFiltersOnChange?: (filters: Partial<SearchFilters>) => void;
  onShowToast?: (text: string) => void;
};

export const shouldRenderCallout = (
  callout: PetInfoQuestion,
  dogSet: DogSet,
  calloutAnswer: string | null
): boolean => {
  // Check confirmation condition first — calloutAnswer drives this, not storage
  if (callout.id === CALLOUT_IDS.DOG_RESPONSE_CONFIRMATION) {
    return (
      !!calloutAnswer &&
      [CALLOUT_RESPONSES.YES, CALLOUT_RESPONSES.UNSURE].some((r) => r === calloutAnswer)
    );
  }

  if (hasRespondedToCallout(callout.id, dogSet)) return false;

  return callout.id === CALLOUT_IDS.DOG_INTERACTION && !calloutAnswer;
};

type UsePetInfoCalloutCoreReturn = {
  petInfoCallout: PetInfoQuestionCallout | undefined;
  calloutAnswer: string | null;
  stableDogSet: DogSet;
  handleChange: (question: PetInfoQuestion, value: string) => void;
  handleClose: (question: PetInfoQuestion) => void;
};

export const usePetInfoCalloutCore = ({
  callouts,
  dogSet,
  updateFiltersOnChange,
  onShowToast,
}: UsePetInfoCalloutCoreParams): UsePetInfoCalloutCoreReturn => {
  const [calloutAnswer, setCalloutAnswer] = useState<string | null>(null);

  const petInfoCallout = useMemo(() => extractPetInfoCallout(callouts), [callouts]);

  const updatePetInfo = useApiPetCalloutResponseCreate();
  const { isAuthenticated } = useCurrentUserShim();
  const { pets: ownPets } = useAuthedUserSummary({
    query: { enabled: !!isAuthenticated },
  });

  const safeDogSet = {
    ...dogSet,
    petIds: dogSet.petIds.filter((e) => e !== ''),
  };
  const dogSetKey = `${safeDogSet.petIds.join(',')}:${safeDogSet.dogCount}:${
    safeDogSet.puppyCount
  }`;

  // Stable reference — only changes when the dog set's identity changes, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDogSet = useMemo(() => safeDogSet, [dogSetKey]);

  useEffect(() => {
    setCalloutAnswer(null);

    // If the user doesn't have dogs (searching with pet count)
    // we set the filter according to their previous answer (if they answered before)
    if (stableDogSet.petIds.length === 0) {
      const response = getCalloutResponse(CALLOUT_IDS.DOG_INTERACTION, stableDogSet);

      if (!response) return;

      if (
        [CALLOUT_RESPONSES.YES, CALLOUT_RESPONSES.DISMISSED].some((r) => r === response.response)
      ) {
        updateFiltersOnChange?.({ personDoesNotHaveDogs: false });
      } else if (
        [CALLOUT_RESPONSES.NO, CALLOUT_RESPONSES.UNSURE].some((r) => r === response.response)
      ) {
        updateFiltersOnChange?.({ personDoesNotHaveDogs: true });
      }
    }
  }, [stableDogSet, updateFiltersOnChange]);

  const handleChange = useCallback(
    (petInfoQuestion: PetInfoQuestion, value: string) => {
      if (!petInfoCallout?.slug || !petInfoQuestion.id) {
        return;
      }

      // Update search filter based on answer
      // "no" → personDoesNotHaveDogs: true (dog does NOT get along with others)
      // "yes"/"unsure" → personDoesNotHaveDogs: false (dog DOES get along with others)
      const personDoesNotHaveDogs = value === CALLOUT_RESPONSES.NO;

      const handleUnauthAnswer = (): void => {
        storeCalloutResponse(petInfoQuestion.id, value, stableDogSet);
        setCalloutAnswer(value);
        if (personDoesNotHaveDogs) {
          onShowToast?.(t`Thanks for telling us! We updated your results.`);
          updateFiltersOnChange?.({ personDoesNotHaveDogs });
        }
      };

      // Only call the endpoint to update pet info if the user has 1 pet.
      // Also verify the pet belongs to the current user (guards against shared URLs with foreign pet IDs).
      const ownsPet =
        stableDogSet.petIds &&
        stableDogSet.petIds.length === 1 &&
        !!ownPets?.some((pet) => pet.pk.toString() === stableDogSet.petIds[0]);

      if (ownsPet) {
        updatePetInfo.mutate(
          {
            data: {
              calloutSlug: petInfoCallout.slug,
              questionId: petInfoQuestion.id,
              answer: value,
              petId: String(stableDogSet.petIds[0]),
            },
          },
          {
            onSuccess: () => {
              setCalloutAnswer(value);
              if (personDoesNotHaveDogs) {
                updateFiltersOnChange?.({ personDoesNotHaveDogs });
                onShowToast?.(
                  t`Thanks for telling us! We updated their pet profile and refined your results.`
                );
              }
            },
            onError: () => {
              // If API call fails, store locally to prevent showing callout again
              // Profile was not updated, so use the generic toast text
              handleUnauthAnswer();
            },
          }
        );
      } else {
        // Store the response locally.
        // This handles: multi-pet users and cases where we don't call the API.
        handleUnauthAnswer();
      }
    },
    // updatePetInfo is a stable reference per react-query
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [petInfoCallout, stableDogSet, updateFiltersOnChange, ownPets, onShowToast]
  );

  const handleClose = useCallback(
    (petInfoQuestion: PetInfoQuestion) => {
      setCalloutAnswer(CALLOUT_RESPONSES.DISMISSED);
      storeCalloutResponse(petInfoQuestion.id, CALLOUT_RESPONSES.DISMISSED, stableDogSet);
    },
    [stableDogSet]
  );

  return {
    petInfoCallout,
    calloutAnswer,
    stableDogSet,
    handleChange,
    handleClose,
  };
};
